import { useEffect, useRef, useState } from 'react';
import { batch } from 'react-redux';

// third-party
import { useIntl } from 'react-intl';

// project imports
import { useDispatch, useSelector } from 'store';
import { openSnackbar } from 'store/slices/snackbar';
import { updateSideMenu, updatePermissionTree, getAllPermissionTreeValues } from 'store/slices/main';
import { ApiGetSettingsWorkflow } from 'store/app/api-settings';
import { ApiAppSettingsGet } from 'store/app/admin/app-setting';
import { ApiBusinessOpportunityTypesGetCollection } from 'store/app/workmgmt/busopp-type';
import { ApiWorkOrderTypesGetCollection } from 'store/app/workmgmt/workorder-type';
import { WORKMGMT_ENTITY_TYPE_FETCH_LIMIT } from 'store/app/workmgmt/entity-type-tree';
import { businessOpportunityActions } from 'store/app/workmgmt/busopp';
import { workOrderActions } from 'store/app/workmgmt/workorder';
import { taskActions } from 'store/app/workmgmt/task';
import { followUpActions } from 'store/app/workmgmt/followup';
import { followUpStepActions } from 'store/app/workmgmt/followup-step';
import {
    buildBusinessOpportunityPermissionUpdates,
    buildWorkOrderPermissionUpdates
} from 'config/authorization/build-workmgmt-permissions';
import { buildBusinessOpportunitySideMenu, buildWorkOrderSideMenu } from 'config/navigation/build-workmgmt-menu';
import Loader from 'ui-component/Loader';

// project hooks
import useAuth from 'hooks/useAuth';
import useSignOutTimer from 'hooks/useSignOutTimer';
import useConfig from 'hooks/useConfig';
import { createTenantAppearanceRequestTracker } from 'contexts/tenantAppearance';

// ==============================|| BACKGROUND LISTENER ||============================== //
// *** IMPORTANT ***
// Side menu and permission tree components will be rendered from the main store,
// but they will dynamically load certain sub-components children from the DB
// Hence the importance of the IDs used in the useEffect hook

const findPermissionNode = (permissionNodes = [], targetValue = '') =>
    permissionNodes.reduce((foundNode, permissionNode) => {
        if (foundNode) {
            return foundNode;
        }

        if (permissionNode?.value === targetValue) {
            return permissionNode;
        }

        if (permissionNode?.children?.length > 0) {
            return findPermissionNode(permissionNode.children, targetValue);
        }

        return null;
    }, null);

const hasDynamicWorkManagementPermissions = (permissionTree = []) => {
    const businessOpportunityNode = findPermissionNode(permissionTree, 'workmgmt-busopp');
    const workOrderNode = findPermissionNode(permissionTree, 'workmgmt-workorder');

    return Boolean(businessOpportunityNode?.children?.length > 0 && workOrderNode?.children?.length > 0);
};

const DataLoader = () => {
    const dispatch = useDispatch();
    const intl = useIntl();
    const { user, isLoggedIn, updateUser, logout, keepMeLoggedIn } = useAuth();
    const { applyTenantBranding, resetTenantPresetColor } = useConfig();
    const [appearanceIsLoading, setAppearanceIsLoading] = useState(true);
    const tenantAppearanceRequestTracker = useRef(createTenantAppearanceRequestTracker());
    const tenantConnectionName = process.env.REACT_APP_CONN || 'default';

    const { permissionTree, appIsLoading } = useSelector((state) => state.main);
    const { list: busOppTypeList, isLoading: busOppTypeIsLoading } = useSelector((state) => state.businessOpportunityType);
    const { list: workOrderTypeList, isLoading: workOrderTypeIsLoading } = useSelector((state) => state.workOrderType);
    const { transitions: busOppTransitions } = useSelector((state) => state.businessOpportunity);
    const { transitions: workOrderTransitions } = useSelector((state) => state.workOrder);
    const { transitions: taskTransitions } = useSelector((state) => state.task);

    useEffect(() => {
        const requestTracker = tenantAppearanceRequestTracker.current;
        const requestId = requestTracker.begin();

        if (!isLoggedIn) {
            resetTenantPresetColor();
            setAppearanceIsLoading(false);
            return undefined;
        }

        setAppearanceIsLoading(true);
        ApiAppSettingsGet(
            (response) => {
                if (!requestTracker.isCurrent(requestId)) return;
                applyTenantBranding(response);
                setAppearanceIsLoading(false);
            },
            (error) => {
                if (!requestTracker.isCurrent(requestId)) return;
                setAppearanceIsLoading(false);
                dispatch(
                    openSnackbar({
                        open: true,
                        message: error || intl.formatMessage({ id: 'ui-comp-snackbar-genericerror-text' }),
                        variant: 'alert',
                        alert: { color: 'error' }
                    })
                );
            }
        );

        return () => {
            if (requestTracker.isCurrent(requestId)) {
                requestTracker.invalidate();
            }
        };
        // applyTenantBranding and resetTenantPresetColor are context callbacks that only update local UI state.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoggedIn, tenantConnectionName]);

    useEffect(() => {
        if (!isLoggedIn) return;

        ApiGetSettingsWorkflow({
            successCallback: (response) =>
                batch(() => {
                    dispatch(businessOpportunityActions?.setPlaces(response?.BusinessOpportunity?.places));
                    dispatch(businessOpportunityActions?.setTransitions(response?.BusinessOpportunity?.transitions));
                    dispatch(workOrderActions?.setPlaces(response?.WorkOrder?.places));
                    dispatch(workOrderActions?.setTransitions(response?.WorkOrder?.transitions));
                    dispatch(taskActions?.setPlaces(response?.Task?.places));
                    dispatch(taskActions?.setTransitions(response?.Task?.transitions));
                    dispatch(followUpActions?.setPlaces(response?.FollowUp?.places));
                    dispatch(followUpActions?.setTransitions(response?.FollowUp?.transitions));
                    dispatch(followUpStepActions?.setPlaces(response?.FollowUpStep?.places));
                    dispatch(followUpStepActions?.setTransitions(response?.FollowUpStep?.transitions));
                }),
            errorCallback: (error) =>
                dispatch(
                    openSnackbar({
                        open: true,
                        message: error || intl.formatMessage({ id: 'ui-comp-snackbar-genericerror-text' }),
                        variant: 'alert',
                        alert: { color: 'error' }
                    })
                )
        });
        ApiBusinessOpportunityTypesGetCollection({
            limit: WORKMGMT_ENTITY_TYPE_FETCH_LIMIT,
            errorCallback: (error) =>
                dispatch(
                    openSnackbar({
                        open: true,
                        message: error || intl.formatMessage({ id: 'ui-comp-snackbar-genericerror-text' }),
                        variant: 'alert',
                        alert: { color: 'error' }
                    })
                )
        });
        ApiWorkOrderTypesGetCollection({
            limit: WORKMGMT_ENTITY_TYPE_FETCH_LIMIT,
            errorCallback: (error) =>
                dispatch(
                    openSnackbar({
                        open: true,
                        message: error || intl.formatMessage({ id: 'ui-comp-snackbar-genericerror-text' }),
                        variant: 'alert',
                        alert: { color: 'error' }
                    })
                )
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoggedIn]);

    useEffect(
        () =>
            batch(() => {
                if (busOppTypeList?.length > 0) {
                    dispatch(updateSideMenu(buildBusinessOpportunitySideMenu(busOppTypeList)));
                }
                if (busOppTypeList?.length > 0 && busOppTransitions?.length > 0 && taskTransitions?.length > 0) {
                    dispatch(
                        updatePermissionTree(
                            buildBusinessOpportunityPermissionUpdates({
                                busOppTypeList,
                                busOppTransitions,
                                taskTransitions,
                                intl
                            })
                        )
                    );
                }
            }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [busOppTypeList, busOppTransitions, taskTransitions]
    );

    useEffect(
        () =>
            batch(() => {
                if (workOrderTypeList?.length > 0) {
                    dispatch(updateSideMenu(buildWorkOrderSideMenu(workOrderTypeList)));
                }
                if (workOrderTypeList?.length > 0 && workOrderTransitions?.length > 0 && taskTransitions?.length > 0) {
                    dispatch(
                        updatePermissionTree(
                            buildWorkOrderPermissionUpdates({
                                workOrderTypeList,
                                workOrderTransitions,
                                taskTransitions,
                                intl
                            })
                        )
                    );
                }
            }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [workOrderTypeList, workOrderTransitions, taskTransitions]
    );

    useEffect(() => {
        const workManagementDataReady =
            !busOppTypeIsLoading &&
            !workOrderTypeIsLoading &&
            busOppTransitions?.length > 0 &&
            workOrderTransitions?.length > 0 &&
            taskTransitions?.length > 0;

        if (user?.username !== 'sysadmin' || !workManagementDataReady || !hasDynamicWorkManagementPermissions(permissionTree)) {
            return;
        }

        const nextAuthorizations = getAllPermissionTreeValues(permissionTree);

        if (JSON.stringify(nextAuthorizations) !== JSON.stringify(user?.authorizations || [])) {
            updateUser({ authorizations: nextAuthorizations });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        user?.username,
        permissionTree,
        busOppTypeIsLoading,
        workOrderTypeIsLoading,
        busOppTransitions,
        workOrderTransitions,
        taskTransitions
    ]);

    useSignOutTimer(() => dispatch(logout), isLoggedIn, keepMeLoggedIn);

    return (appearanceIsLoading || busOppTypeIsLoading || workOrderTypeIsLoading || appIsLoading) && <Loader />;
};

export default DataLoader;
